import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { Progress } from '@/components/ui/progress';
import { CheckCircle2, Settings, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface ConfigurationProgressProps {
  total: number;
  configured: number;
  onComplete?: () => void;
}

export function ConfigurationProgress({ total, configured, onComplete }: ConfigurationProgressProps) {
  useLanguage();
  const draft = total - configured;
  const percentage = total > 0 ? (configured / total) * 100 : 0;
  const isFullyConfigured = configured === total && total > 0;
  const hasNoneConfigured = configured === 0 && total > 0;

  // Determine status icon
  const StatusIcon = isFullyConfigured ? CheckCircle2 : hasNoneConfigured ? Settings : AlertCircle;
  const iconColor = 'text-[#2563EB]';

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl sm:rounded-2xl p-3 sm:p-4 shadow-[0_4px_10px_rgba(0,0,0,0.03)] space-y-2 sm:space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-1.5">
        <div className="flex items-center gap-2">
          <StatusIcon className={`w-4 h-4 sm:w-5 sm:h-5 ${iconColor}`} />
          <h3 className="text-[15px] sm:text-base font-semibold text-[#1F1F1F]">
            {translateInterfaceText("Progresso de Configuração")}</h3>
        </div>
        
        {/* Status Badge - Show only draft badge */}
        {draft > 0 && (
          <span className="text-[11px] sm:text-xs font-medium px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-lg bg-[#FFF4CC] text-[#92400E]">
            {draft} {" "}{translateInterfaceText("em rascunho")}</span>
        )}
        {isFullyConfigured && (
          <span className="text-[11px] sm:text-xs font-medium px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-lg bg-[#2563EB] text-white">
            {translateInterfaceText("Concluído")}</span>
        )}
      </div>

      {/* SubLabel */}
      <p className="text-[12px] sm:text-[13px] text-[#6B7280] mt-1">
        {configured} {" "}{translateInterfaceText("de")}{" "}{total} {total === 1 ? translateInterfaceText("plataforma configurada") : translateInterfaceText("plataformas configuradas")}
      </p>
      
      {/* Progress Bar */}
      <div className="relative w-full h-[6px] bg-[#F3F4F6] rounded-[6px] overflow-hidden mt-3">
        <div 
          className="absolute top-0 left-0 h-full bg-[#2563EB] rounded-[6px] transition-all duration-300 ease-out relative overflow-hidden"
          style={{ width: `${percentage}%` }}
        >
          {/* Shimmer effect overlay */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)',
              width: '50%',
              animation: 'shimmer 2.5s infinite ease-in-out 1.5s'
            }}
          />
        </div>
      </div>

      {/* Footer */}
      {!isFullyConfigured && (
        <div className="flex items-center justify-between gap-2 sm:gap-3 pt-1 flex-wrap">
          <p className="text-[11px] sm:text-xs text-[#6B7280] flex-1 min-w-[160px] sm:min-w-[200px]">
            {translateInterfaceText("Finalize sua configuração para ativar o rastreamento.")}</p>
          {onComplete && (
            <Button
              onClick={onComplete}
              className="h-7 sm:h-8 px-3 sm:px-4 text-[13px] sm:text-sm font-medium bg-[#2563EB] hover:bg-[#2563EB]/90 text-white rounded-lg transition-colors duration-200"
            >
              {translateInterfaceText("Concluir")}</Button>
          )}
        </div>
      )}
    </div>
  );
}
